import {
  Bookmark,
  BorderStyle,
  convertInchesToTwip,
  Document,
  ExternalHyperlink,
  HeadingLevel,
  ImageRun,
  InternalHyperlink,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  WidthType,
} from "docx";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";
import { downloadBlob } from "~/helpers";
import { preprocessFootnotes } from "~/utils/markdownFootnotes";
import {
  ensureBoldHeadingSpacing,
  ensureMarkdownNewlines,
  normalizeLiteralNewlines,
} from "~/utils/markdownTerms";

const fontFamilyMap: Record<string, { body: string; code: string }> = {
  sans: { body: "Arial", code: "Consolas" },
  serif: { body: "Times New Roman", code: "Consolas" },
  mono: { body: "Courier New", code: "Courier New" },
};

const lineSpacingMap: Record<string, number> = {
  "1": 1.0,
  "1.5": 1.5,
  "2": 2.0,
};

export const getExportStyles = (fontFamily?: string, lineSpacing?: string) => {
  const fonts =
    fontFamily && fontFamilyMap[fontFamily]
      ? fontFamilyMap[fontFamily]
      : { body: "Times New Roman", code: "Consolas" };

  const lineHeight =
    lineSpacing && lineSpacingMap[lineSpacing]
      ? lineSpacingMap[lineSpacing]
      : 1.5;

  return {
    fonts,
    lineHeight,
    colors: {
      text: "#000000",
      heading: "#000000",
      blockquote: "#71717A",
      code: "#FFFFFF",
      codeBackground: "#1F2937",
      link: "#000000",
    },
    sizes: {
      body: 12, // px
      h1: 22,
      h2: 18,
      h3: 16,
      h4: 14,
      h5: 13,
      h6: 12,
      code: 12,
    },
    spacing: {
      h1: { before: 22, after: 10 },
      h2: { before: 16, after: 8 },
      h3: { before: 14, after: 8 },
      h4: { before: 12, after: 6 },
      h5: { before: 10, after: 6 },
      h6: { before: 8, after: 4 },
      paragraph: { after: 4 },
      list: { before: 2, after: 2, itemGap: 6 },
    },
  };
};

// Unified style configuration for both PDF and DOCX exports
export const EXPORT_STYLES = getExportStyles();

const toDocxSpacing = (pixels: number, minPixels = 0) =>
  Math.round(Math.max(pixels, minPixels) * 20);

type ExportStyles = ReturnType<typeof getExportStyles>;

// Helper to convert markdown AST nodes to docx elements
function convertNodeToDocx(
  node: any,
  styles: ExportStyles,
  listDepth = 0
): any[] {
  const results: any[] = [];

  if (!node) return results;

  switch (node.type) {
    case "heading":
      results.push(createHeading(node, styles));
      break;
    case "paragraph": {
      const imageChildren = node.children?.filter(
        (child: any) => child.type === "image"
      );
      const nonImageChildren = node.children?.filter(
        (child: any) => child.type !== "image"
      );
      if (nonImageChildren?.length > 0) {
        results.push(
          createParagraph(
            { ...node, children: nonImageChildren },
            styles,
            listDepth
          )
        );
      }
      imageChildren?.forEach((imgChild: any) => {
        results.push(createImageParagraph(imgChild));
      });
      break;
    }
    case "list":
      results.push(...createList(node, styles, listDepth));
      break;
    case "table":
      results.push(createTable(node, styles));
      break;
    case "blockquote":
      results.push(...createBlockquote(node, styles));
      break;
    case "thematicBreak":
      results.push(createThematicBreak());
      break;
    case "code":
      results.push(createCodeBlock(node, styles));
      break;
    case "image":
      results.push(createImageParagraph(node));
      break;
    default:
      if (node.children) {
        node.children.forEach((child: any) => {
          results.push(...convertNodeToDocx(child, styles, listDepth));
        });
      }
      break;
  }

  return results;
}

function createHeading(node: any, styles: ExportStyles): Paragraph {
  const level = node.depth as 1 | 2 | 3 | 4 | 5 | 6;
  const headingLevels = [
    HeadingLevel.HEADING_1,
    HeadingLevel.HEADING_2,
    HeadingLevel.HEADING_3,
    HeadingLevel.HEADING_4,
    HeadingLevel.HEADING_5,
    HeadingLevel.HEADING_6,
  ];

  const sizeKey = `h${level}` as keyof typeof styles.sizes;
  const spacingKey = `h${level}` as keyof typeof styles.spacing;

  const fontSize = level === 2 ? 15 : styles.sizes[sizeKey];

  const headingChildren: any[] = [];
  let underlineDepth = 0;

  node.children.forEach((child: any) => {
    if (child.type === "html") {
      const tag = (child.value || "").trim().toLowerCase();
      if (tag === "<u>") {
        underlineDepth++;
        return;
      }
      if (tag === "</u>") {
        underlineDepth = Math.max(0, underlineDepth - 1);
        return;
      }
    }

    const underlineProps =
      underlineDepth > 0 ? { underline: { type: "single" as const } } : {};

    if (child.type === "text") {
      headingChildren.push(
        new TextRun({
          text: child.value,
          font: styles.fonts.body,
          size: fontSize * 2,
          bold: true,
          color: styles.colors.heading.replace("#", ""),
          ...underlineProps,
        })
      );
    } else {
      headingChildren.push(
        ...convertInlineNodes([child], styles, underlineDepth)
      );
    }
  });

  return new Paragraph({
    heading: headingLevels[level - 1],
    children: headingChildren,
    spacing: {
      before: toDocxSpacing((styles.spacing[spacingKey] as any).before),
      after: toDocxSpacing((styles.spacing[spacingKey] as any).after, 6),
    },
  });
}

function createParagraph(
  node: any,
  styles: ExportStyles,
  listDepth = 0
): Paragraph {
  return new Paragraph({
    children: convertInlineNodes(node.children, styles),
    spacing: {
      after: toDocxSpacing(styles.spacing.paragraph.after, 10),
    },
    indent:
      listDepth > 0
        ? {
            left: convertInchesToTwip(0.5 * listDepth),
          }
        : undefined,
  });
}

export function createList(
  node: any,
  styles: ExportStyles,
  depth = 0
): Paragraph[] {
  const results: Paragraph[] = [];
  const isOrdered = node.ordered;
  // Honor the explicit `start` attribute from the markdown AST so that
  // ordered lists split across headings/paragraphs keep their authored
  // numbering (e.g. `25.` continues after a heading) instead of resetting
  // to `1.` for every new list node.
  const startNumber =
    isOrdered && typeof node.start === "number" ? node.start : 1;

  node.children.forEach((item: any, index: number) => {
    if (item.type === "listItem") {
      const bullet = isOrdered ? `${startNumber + index}.` : "•";

      item.children.forEach((child: any, childIndex: number) => {
        if (child.type === "paragraph") {
          const paragraphChildren: any[] = [
            new TextRun({
              text: childIndex === 0 ? `${bullet} ` : "",
              font: styles.fonts.body,
            }),
            ...convertInlineNodes(child.children, styles),
          ];

          // For top-level ordered list items, wrap in a Bookmark so that
          // internal links like [5](#ref-5) navigate to the correct item.
          const finalChildren =
            isOrdered && depth === 0 && childIndex === 0
              ? [
                  new Bookmark({
                    id: `ref-${startNumber + index}`,
                    children: paragraphChildren,
                  }),
                ]
              : paragraphChildren;

          results.push(
            new Paragraph({
              children: finalChildren,
              spacing: {
                before: toDocxSpacing(styles.spacing.list.before, 2),
                after: toDocxSpacing(styles.spacing.list.itemGap, 6),
              },
              indent: {
                left: convertInchesToTwip(0.5 * depth),
                hanging: depth > 0 ? convertInchesToTwip(0.25) : undefined,
              },
            })
          );
        } else if (child.type === "list") {
          results.push(...createList(child, styles, depth + 1));
        }
      });
    }
  });

  return results;
}

function createTable(node: any, styles: ExportStyles): Table {
  const rows: TableRow[] = [];

  // Get number of columns from first row
  const numColumns = node.children[0]?.children?.length || 1;

  const pageWidthTwips = 9000; // Usable page width in twips
  const columnWidthTwips = Math.floor(pageWidthTwips / numColumns);

  const getNodeText = (node: any): string => {
    if (node.value !== undefined) return String(node.value);
    if (node.children) return node.children.map(getNodeText).join("");
    return "";
  };

  node.children.forEach((row: any) => {
    if (row.type === "tableRow") {
      const cells: TableCell[] = row.children.map(
        (cell: any, cellIndex: number) => {
          const inlineChildren = convertInlineNodes(cell.children, styles);
          let paragraphChildren: (
            | TextRun
            | ExternalHyperlink
            | InternalHyperlink
            | Bookmark
          )[] = inlineChildren;
          if (cellIndex === 0) {
            const cellText = getNodeText(cell).trim();
            if (/^\d+$/.test(cellText)) {
              paragraphChildren = [
                new Bookmark({
                  id: `ref-${cellText}`,
                  children: inlineChildren,
                }),
              ];
            }
          }
          return new TableCell({
            children: [
              new Paragraph({
                children: paragraphChildren,
              }),
            ],
            width: {
              size: columnWidthTwips,
              type: WidthType.DXA,
            },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
            },
          });
        }
      );

      rows.push(new TableRow({ children: cells }));
    }
  });

  return new Table({
    rows,
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },
    layout: TableLayoutType.FIXED,
    columnWidths: Array(numColumns).fill(columnWidthTwips),
    margins: {
      bottom: 240,
    },
  });
}

function createBlockquote(node: any, styles: ExportStyles): Paragraph[] {
  const results: Paragraph[] = [];

  node.children.forEach((child: any) => {
    if (child.type === "paragraph") {
      results.push(
        new Paragraph({
          children: convertInlineNodes(child.children, styles),
          indent: {
            left: convertInchesToTwip(0.5),
          },
          border: {
            left: {
              color: styles.colors.blockquote.replace("#", ""),
              space: 1,
              style: BorderStyle.SINGLE,
              size: 24,
            },
          },
        })
      );
    }
  });

  return results;
}

function createThematicBreak(): Paragraph {
  return new Paragraph({
    border: {
      bottom: {
        color: "CCCCCC",
        space: 1,
        style: BorderStyle.SINGLE,
        size: 6,
      },
    },
    spacing: {
      before: 200,
      after: 200,
    },
  });
}

function createCodeBlock(node: any, styles: ExportStyles): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: node.value,
        font: styles.fonts.code,
        size: styles.sizes.code * 2,
        color: styles.colors.code.replace("#", ""),
      }),
    ],
    shading: {
      fill: styles.colors.codeBackground.replace("#", ""),
    },
  });
}

const MAX_DOCX_IMAGE_WIDTH = 500;

export function parseBase64DataUrl(dataUrl: string): {
  mimeType: string;
  data: Uint8Array;
} | null {
  const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) return null;
  const mimeType = match[1];
  const base64 = match[2];
  try {
    const binaryStr = atob(base64);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return { mimeType, data: bytes };
  } catch {
    return null;
  }
}

export function getImageTypeFromMime(
  mimeType: string
): "jpg" | "png" | "gif" | "bmp" {
  if (mimeType.includes("jpeg") || mimeType.includes("jpg")) return "jpg";
  if (mimeType.includes("gif")) return "gif";
  if (mimeType.includes("bmp")) return "bmp";
  return "png";
}

const DEFAULT_IMAGE_ASPECT_RATIO = 0.75;

/**
 * Extract image dimensions from raw binary data by reading format headers.
 * Supports PNG, GIF, and JPEG formats.
 */
export function getImageDimensionsFromData(
  data: Uint8Array,
  mimeType: string
): { width: number; height: number } | null {
  if (mimeType.includes("png") && data.length >= 24) {
    const width =
      (data[16] << 24) | (data[17] << 16) | (data[18] << 8) | data[19];
    const height =
      (data[20] << 24) | (data[21] << 16) | (data[22] << 8) | data[23];
    if (width > 0 && height > 0) return { width, height };
  }

  if (mimeType.includes("gif") && data.length >= 10) {
    const width = data[6] | (data[7] << 8);
    const height = data[8] | (data[9] << 8);
    if (width > 0 && height > 0) return { width, height };
  }

  if (
    (mimeType.includes("jpeg") || mimeType.includes("jpg")) &&
    data.length >= 2
  ) {
    let offset = 2;
    while (offset < data.length - 1) {
      if (data[offset] !== 0xff) break;
      const marker = data[offset + 1];
      if (
        marker >= 0xc0 &&
        marker <= 0xcf &&
        marker !== 0xc4 &&
        marker !== 0xc8 &&
        marker !== 0xcc
      ) {
        if (offset + 9 <= data.length) {
          const height = (data[offset + 5] << 8) | data[offset + 6];
          const width = (data[offset + 7] << 8) | data[offset + 8];
          if (width > 0 && height > 0) return { width, height };
        }
        break;
      }
      if (offset + 3 < data.length) {
        const segLen = (data[offset + 2] << 8) | data[offset + 3];
        offset += 2 + segLen;
      } else {
        break;
      }
    }
  }

  return null;
}

function createImageParagraph(node: {
  url?: string;
  alt?: string;
  width?: number;
  height?: number;
}): Paragraph {
  const url = node.url || "";
  const parsed = parseBase64DataUrl(url);

  if (!parsed) {
    return new Paragraph({
      children: [
        new TextRun({
          text: node.alt ? `[Image: ${node.alt}]` : "[Image]",
          italics: true,
          color: "888888",
        }),
      ],
      spacing: { after: 200 },
    });
  }

  const imageType = getImageTypeFromMime(parsed.mimeType);

  // Try node metadata first, then extract from binary data, then use default
  let aspectRatio = DEFAULT_IMAGE_ASPECT_RATIO;
  let sourceWidth = MAX_DOCX_IMAGE_WIDTH;
  if (node.width && node.height) {
    aspectRatio = node.height / node.width;
    sourceWidth = node.width;
  } else {
    const dims = getImageDimensionsFromData(parsed.data, parsed.mimeType);
    if (dims) {
      aspectRatio = dims.height / dims.width;
      sourceWidth = dims.width;
    }
  }

  const displayWidth = Math.min(MAX_DOCX_IMAGE_WIDTH, sourceWidth);
  const displayHeight = Math.round(displayWidth * aspectRatio);

  return new Paragraph({
    children: [
      new ImageRun({
        type: imageType,
        data: parsed.data,
        transformation: {
          width: displayWidth,
          height: displayHeight,
        },
        altText: {
          title: node.alt || "Image",
          description: node.alt || "Image",
          name: node.alt || "Image",
        },
      }),
    ],
    spacing: { after: 200 },
  });
}

function convertInlineNodes(
  nodes: any[],
  styles: ExportStyles,
  underlineDepth = 0,
  bold = false,
  italics = false
): (TextRun | ExternalHyperlink | InternalHyperlink)[] {
  const results: (TextRun | ExternalHyperlink | InternalHyperlink)[] = [];

  if (!nodes) return results;

  let depth = underlineDepth;

  nodes.forEach((node) => {
    if (node.type === "html") {
      const tag = (node.value || "").trim().toLowerCase();
      if (tag === "<u>") {
        depth++;
        return;
      }
      if (tag === "</u>") {
        depth = Math.max(0, depth - 1);
        return;
      }
    }

    const underlineProps =
      depth > 0 ? { underline: { type: "single" as const } } : {};

    switch (node.type) {
      case "text":
        results.push(
          new TextRun({
            text: node.value,
            font: styles.fonts.body,
            size: styles.sizes.body * 2,
            bold: bold || undefined,
            italics: italics || undefined,
            ...underlineProps,
          })
        );
        break;
      case "strong":
        results.push(
          ...convertInlineNodes(node.children, styles, depth, true, italics)
        );
        break;
      case "emphasis":
        results.push(
          ...convertInlineNodes(node.children, styles, depth, bold, true)
        );
        break;
      case "inlineCode":
        results.push(
          new TextRun({
            text: node.value,
            font: styles.fonts.code,
            size: styles.sizes.code * 2,
            shading: {
              fill: styles.colors.codeBackground.replace("#", ""),
            },
            color: styles.colors.code.replace("#", ""),
          })
        );
        break;
      case "link": {
        const rawLinkText = node.children[0]?.value || node.url;
        const linkText = /^\d+$/.test(rawLinkText)
          ? `[${rawLinkText}]`
          : rawLinkText;
        if (node.url.startsWith("#")) {
          results.push(
            new InternalHyperlink({
              anchor: node.url.slice(1),
              children: [
                new TextRun({
                  text: linkText,
                  style: "Hyperlink",
                  font: styles.fonts.body,
                  size: styles.sizes.body * 2,
                }),
              ],
            })
          );
        } else {
          results.push(
            new ExternalHyperlink({
              link: node.url,
              children: [
                new TextRun({
                  text: linkText,
                  style: "Hyperlink",
                  font: styles.fonts.body,
                  size: styles.sizes.body * 2,
                }),
              ],
            })
          );
        }
        break;
      }
      case "break":
        results.push(new TextRun({ text: "", break: 1 }));
        break;
      default:
        // Handle nested inline elements
        if (node.children) {
          results.push(
            ...convertInlineNodes(node.children, styles, depth, bold, italics)
          );
        }
        break;
    }
  });

  return results;
}

export async function downloadMarkdownAsDocx(
  markdown: string,
  fileName: string,
  title?: string,
  fontFamily?: string,
  lineSpacing?: string
): Promise<void> {
  // Get export styles with organization settings
  const styles = getExportStyles(fontFamily, lineSpacing);

  const processedMarkdown = ensureBoldHeadingSpacing(
    ensureMarkdownNewlines(
      preprocessFootnotes(normalizeLiteralNewlines(markdown))
    )
  );

  // Add title to the markdown if provided
  const contentWithTitle = title
    ? `# ${title}\n\n${processedMarkdown}`
    : processedMarkdown;

  // Parse markdown to AST
  const processor = unified().use(remarkParse).use(remarkGfm).use(remarkBreaks);

  const tree = processor.parse(contentWithTitle);
  const ast = (await processor.run(tree)) as any;

  // Convert AST to docx elements
  const docxElements: any[] = [];

  if (ast.type === "root" && ast.children) {
    ast.children.forEach((node: any) => {
      docxElements.push(...convertNodeToDocx(node, styles));
    });
  }

  // Create document with custom styles
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: styles.fonts.body,
            size: styles.sizes.body * 2,
          },
        },
      },
      characterStyles: [
        {
          id: "Hyperlink",
          name: "Hyperlink",
          basedOn: "Default Paragraph Font",
          run: {
            color: styles.colors.link.replace("#", ""),
          },
        },
      ],
    },
    sections: [
      {
        children: docxElements,
      },
    ],
  });

  // Generate blob and download
  const blob = await Packer.toBlob(doc);
  await downloadBlob(blob, `${fileName}.docx`);
}
